import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { LocationStrategy } from "@angular/common";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Router } from "@angular/router";
import {
    AppointmentsService,
    GlobalSearchService,
    GlobalsService,
    SleepService,
    TasksService,
    UtilsService
} from "@services";
import { CorporateStore, TimeZoneStore } from "@stores";
import {
    GlobalSearchResultType,
    IGlobalSearchFlatResult,
    IGlobalSearchResult,
    IGlobalSearchResultsModel
} from "@models";
import { SlickAutoCompleteComponent } from "slick-components";
import { TaskEditDialogComponent } from "@app/tasks/tasks-components";
import { AppointmentDialogComponent } from "@app/appointments/appointment-dialog.component";
import { ScheduleAppointmentStore } from "@stores";

const PREFIX_TO_TYPE: Record<string, GlobalSearchResultType> = {
    cus: "customer",
    job: "job",
    bda: "bdAccount",
    bdc: "bdContact",
    sub: "subcontractor",
    ven: "vendor",
    eqp: "equipment",
    inv: "invoice",
    pmt: "payment",
    pay: "payable",
    wo: "workOrder"
};

const KNOWN_PREFIXES = Object.keys(PREFIX_TO_TYPE);

interface ISearchTypeOption {
    code: string;
    label: string;
    type: GlobalSearchResultType | null;
}

@Component({
    selector: "global-search-bar",
    templateUrl: "global-search-bar.component.html",
    styleUrls: ["global-search-bar.component.css"],
    providers: [
        GlobalSearchService,
        // The hosted appointment dialog injects this schedule store (normally provided by the schedule modules);
        // provide our own instance so the dialog works from the header without a schedule view present.
        ScheduleAppointmentStore
    ],
    standalone: false
})
export class GlobalSearchBarComponent implements OnDestroy {

    @ViewChild("autoComplete", { static: false }) autoComplete: SlickAutoCompleteComponent;
    @ViewChild("taskDialogRef") taskDialogRef: TaskEditDialogComponent;
    @ViewChild("appointmentDialogRef") appointmentDialogRef: AppointmentDialogComponent;

    searchText: string;
    searchResults: IGlobalSearchFlatResult[] = [];
    isAsking = false;
    isPaging = false; // true only while prev/next is loading — keeps results visible (unlike isAsking's full reset)
    aiMode = false;
    aiSearched = false; // true once an AI query has returned (drives the "no matches" state)
    // The agent's written answer (complex/aggregate questions), backed so every assignment also refreshes the
    // pre-rendered safe-HTML version the template binds to.
    private _aiAnswer: string | null = null;
    renderedAnswer: SafeHtml | null = null; // _aiAnswer rendered to minimal, safe HTML (bold + line breaks)
    // "Next step" searches the AI returns when it needs more info — rendered as buttons that auto-run the search.
    aiSuggestions: string[] = [];
    // One id per CONVERSATION (not per ask): minted on the first question, reused across follow-ups so the server
    // threads them together, and dropped when the chat is closed or goes idle. Also correlates live SignalR progress.
    aiSessionId: string | null = null;
    inputFocused = false;

    // Auto-close the conversation after 5 minutes of no searching activity (mirrors the server's 5-min sliding
    // expiry of the conversation memory). Reset on every ask / page change; fires endConversation on lapse.
    private static readonly IDLE_TIMEOUT_MS = 5 * 60 * 1000;
    private idleTimer: ReturnType<typeof setTimeout> | null = null;

    // AI results paging — the server returns 25 per page; these track the current page + whether more exist.
    currentPage = 1;
    hasMore = false;
    totalResults = 0;
    lastAiQuery = ""; // the query prev/next pages through (the executed query, not the live textbox)
    // The row you last opened. The panel stays open on click, so we highlight it instead of closing.
    lastSelectedKey: string | null = null;
    // Whether the AI results panel (the dropdown) is showing.
    aiResultsOpen = false;
    // Whether the collapsed "results" indicator (floats below the bar) is showing. The × hides BOTH; the
    // minimize button / opening a result collapses the panel to this indicator; results stay cached either way.
    aiIndicatorVisible = false;

    // Scrolling "marquee" of example queries shown over the empty input to teach what you can ask.
    // Includes boolean queries (and / or / except) now that the AI search supports them.
    readonly examplesText = "Try:        " + [
        "Payments or jobs for a customer",
        "Payables over $1,000 except this month",
        "Work orders for a subcontractor",
        "Unpaid invoices over $5,000",
        "Jobs that aren't closed",
        "Total payments this year"
    ].join("        •        ");

    private static readonly SEARCH_DEBOUNCE_MS = 600;
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    private static readonly TYPE_PERMISSION_FEATURE: Partial<Record<GlobalSearchResultType, string>> = {
        job: "Jobs",
        customer: "Customers",
        bdAccount: "BusinessDevelopmentAccounts",
        bdContact: "BusinessDevelopmentAccounts",
        subcontractor: "Subcontractors",
        vendor: "Vendors",
        equipment: "Equipment",
        invoice: "Invoices",
        payment: "Payments",
        payable: "Payables",
        workOrder: "WorkOrders"
    };

    readonly searchTypeOptions: ISearchTypeOption[] = (<ISearchTypeOption[]>[
        { code: "all", label: "All", type: null },
        { code: "job", label: "Jobs", type: "job" },
        { code: "cus", label: "Customers", type: "customer" },
        { code: "bda", label: "BD Accounts", type: "bdAccount" },
        { code: "bdc", label: "BD Contacts", type: "bdContact" },
        { code: "sub", label: "Subcontractors", type: "subcontractor" },
        { code: "ven", label: "Vendors", type: "vendor" },
        { code: "eqp", label: "Equipment", type: "equipment" },
        { code: "inv", label: "Invoices", type: "invoice" },
        { code: "pmt", label: "Payments", type: "payment" },
        { code: "pay", label: "Payables", type: "payable" },
        { code: "wo", label: "Work Orders", type: "workOrder" }
    ]).filter(o => o.type === null || GlobalSearchBarComponent.userCanSee(o.type));
    selectedSearchType: ISearchTypeOption = this.searchTypeOptions[0];

    private static userCanSee(type: GlobalSearchResultType): boolean {
        const feature = GlobalSearchBarComponent.TYPE_PERMISSION_FEATURE[type];
        if (!feature)
            return false;
        return GlobalsService.checkPermission(feature, "View|Update");
    }
    typeDropdownOpen = false;

    // Corporate / dispatch users search across ALL their branches (the server fans the query over every child
    // company and tags each result with its branch). Opening a cross-branch result switches to that franchise
    // first (there is no single company to open it in). Normal users search their own company and open in place.
    readonly isCorporateUser = GlobalsService.isCorporate;

    constructor(
        private hostElement: ElementRef,
        private router: Router,
        private locationStrategy: LocationStrategy,
        private sanitizer: DomSanitizer,
        private globalSearchService: GlobalSearchService,
        private tasksService: TasksService,
        private appointmentsService: AppointmentsService,
        private corporateStore: CorporateStore,
        private timeZoneStore: TimeZoneStore
    ) { }

    // aiAnswer is a property so every existing `this.aiAnswer = …` assignment also refreshes the pre-rendered
    // safe-HTML version the template binds to. Reads (template *ngIf, collapsedLabel) still see the raw string.
    get aiAnswer(): string | null {
        return this._aiAnswer;
    }
    set aiAnswer(value: string | null) {
        this._aiAnswer = value;
        this.renderedAnswer = value ? this.renderAnswer(value) : null;
    }

    // Renders the agent's answer as minimal, SAFE HTML. The text is model-generated, so HTML-escape it first, then
    // render the light markdown it emits: **double-asterisk** pairs -> bold, `backtick` pairs -> inline code, and
    // newlines -> <br>. A LONE "*" or "`" is deliberately left as-is — a customer/job name can legitimately contain
    // one, and it must not trigger formatting. [^*]/[^`] inside each pair keeps a span from straddling a stray marker.
    private renderAnswer(text: string): SafeHtml {
        const escaped = text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");

        const html = escaped
            .replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>")
            .replace(/`([^`]+?)`/g, "<code>$1</code>")
            .replace(/\r?\n/g, "<br>");

        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    onSearch(rawText: string): void {
        // In AI mode the fast type-ahead is suppressed — the AI query runs on the run button / Enter instead.
        if (this.aiMode)
            return;

        const { resultType: prefixType, query } = this.parsePrefix(rawText ?? "");

        // Typed prefix beats the dropdown; otherwise fall back to the dropdown selection.
        const resultType: GlobalSearchResultType | null =
            prefixType ?? this.selectedSearchType.type;

        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }

        // With no type and no query, clear results immediately
        if (!resultType && !query) {
            this.searchResults = [];
            return;
        }

        this.searchDebounceTimer = setTimeout(() => {
            this.searchDebounceTimer = null;
            this.runSearch(resultType, query);
        }, GlobalSearchBarComponent.SEARCH_DEBOUNCE_MS);
    }

    private async runSearch(resultType: GlobalSearchResultType | null, query: string): Promise<void> {
        // Build the list of types to query. If a specific type was chosen, just that one;
        // otherwise, everything the current user has permission to see.
        const typesToQuery: GlobalSearchResultType[] = resultType
            ? [resultType]
            : this.allowedTypes();

        // Fan out to one HTTP call per type so each one gets its own request-scoped DbContext
        // on the server. Aggregate into a single flat list preserving the display order below.
        const perTypeResults = await Promise.all(
            typesToQuery.map(async type => {
                const prefix = this.typeToPrefix(type);
                const rows = await this.globalSearchService.searchType(prefix, query, this.isCorporateUser);
                return { type, rows: rows ?? [] };
            })
        );

        const byType = new Map<GlobalSearchResultType, IGlobalSearchResult[]>(
            perTypeResults.map(r => [r.type, r.rows])
        );

        this.searchResults = this.flatten(byType);
    }

    // Swap the bar between the fast keyword type-ahead and AI ("find X related to Y") mode.
    toggleAiMode(): void {
        // Leaving AI mode (or re-entering) ends the current conversation so the next one starts clean.
        this.endConversation();
        this.aiMode = !this.aiMode;
        this.typeDropdownOpen = false;
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }
        this.resetAiResults();
    }

    // Wipe the AI panel back to a clean slate (results, answer, suggestions, paging, session state). Does NOT touch
    // the search text — callers that want a full reset clear that themselves.
    private resetAiResults(): void {
        this.searchResults = [];
        this.aiSearched = false;
        this.aiAnswer = null;
        this.aiSuggestions = [];
        this.currentPage = 1;
        this.hasMore = false;
        this.totalResults = 0;
        this.lastAiQuery = "";
        this.lastSelectedKey = null;
        this.aiResultsOpen = false;
        this.aiIndicatorVisible = false;
    }

    // Natural-language "find X related to Y" search (e.g. "payments for job 1234"). Only runs in AI mode, on Enter.
    // Results render in our own panel (not slick-auto-complete, which is type-ahead only), but reuse onSelect navigation.
    async onAskAI(): Promise<void> {
        if (!this.aiMode)
            return;

        const query = (this.searchText ?? "").trim();
        if (!query || this.isAsking)
            return;

        this.lastAiQuery = query;
        this.lastSelectedKey = null;
        await this.fetchAiPage(1);
    }

    // Click a suggested "next step" — drop it into the box and run it. It reuses the same session, so it lands as a
    // follow-up in the conversation the clarifying question was part of. stopPropagation so the document:click
    // handler doesn't fire: running the search clears aiSuggestions, which removes THIS button from the DOM before
    // that handler runs, making its contains(target) check misread the detached node as an outside click and
    // collapse the panel mid-search.
    async askSuggestion(suggestion: string, event?: Event): Promise<void> {
        event?.stopPropagation();
        const query = (suggestion ?? "").trim();
        if (!query || this.isAsking)
            return;

        this.searchText = query;
        this.lastAiQuery = query;
        this.lastSelectedKey = null;
        await this.fetchAiPage(1);
    }

    // ---- AI results paging — the SERVER returns 25 per page; prev/next walk through ALL matches ----

    // currentPage / hasMore / totalResults come from the server response. We page the executed query
    // (lastAiQuery), not the live textbox, so editing the box doesn't change what prev/next walks.
    // A NEW query does the full-loading treatment: reset results/answer and restart the live steps. A page CHANGE
    // (prev/next) keeps the current results, answer, and session VISIBLE and just swaps in the new page's rows when
    // they arrive — so paging never blanks the panel out (which read as the panel "minimizing"/closing).
    private async fetchAiPage(page: number, isPageChange = false): Promise<void> {
        const query = this.lastAiQuery;
        if (!query || this.isAsking || this.isPaging)
            return;

        this.aiResultsOpen = true;
        this.aiIndicatorVisible = true;
        // Any ask or page turn counts as activity — push the 5-minute auto-close back out.
        this.resetIdleTimer();

        if (isPageChange) {
            this.isPaging = true; // disable the nav buttons, but leave the current page's results on screen
        }
        else {
            this.isAsking = true;
            this.aiSearched = false;
            this.searchResults = [];
            this.aiAnswer = null;
            this.aiSuggestions = [];
            // Reuse the conversation's session across asks so follow-ups build on prior turns; mint one lazily on
            // the first question of a new conversation.
            if (!this.aiSessionId)
                this.aiSessionId = UtilsService.newGuid();
        }

        try {
            const resp = await this.globalSearchService.aiSearch(query, this.aiSessionId, page, this.isCorporateUser);
            // The written answer is per-query, not per-page — on a page change keep the prior one if none came back.
            if (!isPageChange || resp?.answer != null)
                this.aiAnswer = resp?.answer ?? null;
            // Suggestions are per-query (the agent path never pages), so only refresh them on the initial ask.
            if (!isPageChange)
                this.aiSuggestions = resp?.suggestions ?? [];
            this.searchResults = this.flattenModel(resp?.results);
            this.currentPage = resp?.page ?? page;
            this.totalResults = resp?.total ?? this.searchResults.length;
            this.hasMore = resp?.hasMore ?? false;
        }
        catch {
            // A failed page change leaves the current page in place; a failed initial query clears to the empty state.
            if (!isPageChange) {
                this.searchResults = [];
                this.aiAnswer = null;
                this.aiSuggestions = [];
            }
            this.hasMore = false;
        }
        finally {
            this.isAsking = false;
            this.isPaging = false;
            this.aiSearched = true;
        }
    }

    nextPage(): void {
        if (this.hasMore && !this.isAsking && !this.isPaging)
            this.fetchAiPage(this.currentPage + 1, true);
    }

    prevPage(): void {
        if (this.currentPage > 1 && !this.isAsking && !this.isPaging)
            this.fetchAiPage(this.currentPage - 1, true);
    }

    // Stable key for the row you last opened (used to highlight it; the panel stays open on click).
    resultKey(result: IGlobalSearchFlatResult): string {
        return `${result.resultType}:${result.uuid ?? result.id}`;
    }

    // The × (on the panel header or on the collapsed indicator) — hide BOTH the panel and the indicator. Results
    // are kept in memory, so clicking back in the search box (onInputFocus) still pulls them up. stopPropagation
    // so the × on the indicator doesn't also fire the indicator's reopen click.
    closeAiResults(event?: Event): void {
        event?.stopPropagation();
        // The × is a full reset: end the conversation, wipe the panel, and clear the search box so the bar is clean.
        this.endConversation();
        this.resetAiResults();
        this.searchText = "";
    }

    // End the current AI conversation: tell the server to forget it and drop our session id so the next question
    // starts a brand-new thread. Also stops the idle timer. Safe to call when no conversation is active.
    private endConversation(): void {
        this.clearIdleTimer();
        const sessionId = this.aiSessionId;
        this.aiSessionId = null;
        if (sessionId)
            void this.globalSearchService.clearAiSession(sessionId);
    }

    private resetIdleTimer(): void {
        this.clearIdleTimer();
        this.idleTimer = setTimeout(() => this.onIdleTimeout(), GlobalSearchBarComponent.IDLE_TIMEOUT_MS);
    }

    private clearIdleTimer(): void {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    // No searching activity for 5 minutes -> auto-close: end the conversation and collapse the panel. The next
    // question mints a fresh session, so it won't try to build on a conversation the server has already expired.
    private onIdleTimeout(): void {
        this.endConversation();
        this.aiResultsOpen = false;
        this.aiIndicatorVisible = false;
    }

    ngOnDestroy(): void {
        this.clearIdleTimer();
    }

    // The minimize button (and opening a result) — collapse the panel down to the floating indicator.
    collapseToIndicator(event?: Event): void {
        event?.stopPropagation();
        this.aiResultsOpen = false;
        this.aiIndicatorVisible = true;
    }

    // Is there a finished AI search we could re-open?
    get hasAiResults(): boolean {
        return this.aiSearched && (this.searchResults.length > 0 || !!this.aiAnswer);
    }

    // Focusing the search box re-opens the last results (if any are cached and the panel was dismissed).
    onInputFocus(): void {
        this.inputFocused = true;
        if (this.aiMode && this.hasAiResults)
            this.aiResultsOpen = true;
    }

    // Re-open the cached results. stopPropagation so the document:click "clicked-outside" handler doesn't
    // fire for this same click and immediately re-close it.
    reopenResults(event?: Event): void {
        event?.stopPropagation();
        this.aiResultsOpen = true;
    }

    // Label for the collapsed "results available" pill.
    get collapsedLabel(): string {
        const n = this.totalResults || this.searchResults.length;
        if (n > 0)
            return `${n} ${n === 1 ? "result" : "results"}`;
        return this.aiAnswer ? "Show answer" : "Show results";
    }

    private flattenModel(model: IGlobalSearchResultsModel | undefined): IGlobalSearchFlatResult[] {
        if (!model)
            return [];

        const byType = new Map<GlobalSearchResultType, IGlobalSearchResult[]>([
            ["job", model.jobs ?? []],
            ["customer", model.customers ?? []],
            ["bdAccount", model.businessDevelopmentAccounts ?? []],
            ["bdContact", model.businessDevelopmentContacts ?? []],
            ["subcontractor", model.subcontractors ?? []],
            ["vendor", model.vendors ?? []],
            ["equipment", model.equipment ?? []],
            ["workOrder", model.workOrders ?? []],
            ["invoice", model.invoices ?? []],
            ["payment", model.payments ?? []],
            ["payable", model.payables ?? []],
            ["estimate", model.estimates ?? []],
            ["task", model.tasks ?? []],
            ["appointment", model.appointments ?? []]
        ]);

        return this.flatten(byType);
    }

    toggleTypeDropdown(): void {
        this.typeDropdownOpen = !this.typeDropdownOpen;
    }

    async selectSearchType(option: ISearchTypeOption): Promise<void> {
        this.selectedSearchType = option;
        this.typeDropdownOpen = false;

        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }

        this.autoComplete?.refreshItems(null);
        await SleepService.sleep();

        if (this.searchText) {
            const { resultType: prefixType, query } = this.parsePrefix(this.searchText);
            const resultType: GlobalSearchResultType | null = prefixType ?? option.type;
            this.autoComplete?.expand();
            await this.runSearch(resultType, query);
        }
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node | null;
        // A click whose target was removed from the DOM by our own change detection (e.g. a "next step" suggestion
        // button that clears itself when clicked) is NOT an outside click — contains() reports false on the detached
        // node and would wrongly collapse the panel. Ignore detached targets so only genuine outside clicks collapse.
        if (target && !target.isConnected)
            return;
        if (target && this.hostElement.nativeElement.contains(target))
            return;

        // Clicked outside the bar — close the type dropdown, and collapse an OPEN panel down to the floating
        // indicator (results stay reachable). Never resurrects an indicator the user already dismissed with ×.
        this.typeDropdownOpen = false;
        if (this.aiResultsOpen) {
            this.aiResultsOpen = false;
            this.aiIndicatorVisible = true;
        }
    }

    private allowedTypes(): GlobalSearchResultType[] {
        const all: GlobalSearchResultType[] = [
            "job", "customer",
            "bdAccount", "bdContact",
            "subcontractor", "vendor", "equipment",
            "invoice", "payment", "payable",
            "workOrder"
        ];
        return all.filter(t => GlobalSearchBarComponent.userCanSee(t));
    }

    async onSelect(result: IGlobalSearchFlatResult): Promise<void> {
        if (!result) return;

        if (this.aiMode) {
            // Opening an AI result navigates the current view and collapses the panel down to the floating
            // indicator (results stay one click away); mark the row we opened.
            this.lastSelectedKey = this.resultKey(result);
            this.collapseToIndicator();
        } else {
            this.searchText = "";
            this.searchResults = [];
        }

        // Tasks / appointments open their OWN dialog instead of navigating away.
        if (result.resultType === "task") {
            await this.openTaskDialog(result.id);
            return;
        }
        if (result.resultType === "appointment") {
            await this.openAppointmentDialog(result);
            return;
        }

        // Corporate/dispatch: results can live in any branch, and the shell can't render entity pages directly —
        // switch to the result's franchise and open it inside the franchise iframe.
        if (this.isCorporateUser) {
            this.openInFranchise(result);
            return;
        }

        const path = this.resultPath(result);
        if (path)
            await this.router.navigate(path);
    }

    // Corporate/dispatch open: switch to the result's branch (so the franchise-page-view iframe loads under that
    // branch's token), then navigate to that entity's franchise page. Mirrors the existing corporate job-open flow.
    private openInFranchise(result: IGlobalSearchFlatResult): void {
        const page = this.franchisePage(result.resultType);
        if (!page || !result.uuid)
            return;

        if (result.companyId && this.corporateStore.selectedFranchise()?.companyId !== result.companyId) {
            this.corporateStore.selectFranchise(result.companyId);
            const franchise = this.corporateStore.franchises()?.find(x => x.companyId === result.companyId);
            if (franchise)
                this.timeZoneStore.setTimeZone(franchise.timeZone);
        }

        this.router.navigate(["/franchise-page-view", page], { queryParams: { uuid: result.uuid } });
    }

    // The inner (per-franchise) route segment each result type opens at — the franchise-page-view iframe loads
    // "#/{segment}/{uuid}".
    private franchisePage(type: GlobalSearchResultType): string | null {
        switch (type) {
            case "job": return "jobs";
            case "customer": return "customers";
            case "invoice": return "invoices";
            case "payment": return "payments";
            case "payable": return "payables";
            case "vendor": return "vendors";
            case "subcontractor": return "subcontractors";
            case "equipment": return "equipment";
            case "workOrder": return "workOrders";
            case "bdAccount":
            case "bdContact": return "businessDevelopment";
        }
        return null;
    }

    private async openTaskDialog(taskId: number): Promise<void> {
        try {
            const taskModel = await this.tasksService.getTask(taskId);
            if (taskModel)
                await this.taskDialogRef.showDialog(taskModel);
        } catch { }
    }

    private async openAppointmentDialog(result: IGlobalSearchFlatResult): Promise<void> {
        try {
            const appt = await this.appointmentsService.getAppointment(result.id);
            // The unified dialog only handles service-tech / subcontractor appointments; showDialog returns
            // undefined for event/employee types, so fall back to opening the parent job in that case.
            const shown = appt ? this.appointmentDialogRef.showDialog(appt) : undefined;
            if (shown) { await shown; return; }
        } catch { }
        if (result.uuid)
            await this.router.navigate(["/jobs", result.uuid]);
    }

    // Open a result in a NEW browser tab — does NOT navigate the current view or collapse the panel.
    openInNewWindow(result: IGlobalSearchFlatResult, event: Event): void {
        event.stopPropagation();
        const path = this.resultPath(result);
        if (!path) return;
        // The app uses HashLocationStrategy, so the openable URL needs the "#" (+ base href) — prepareExternalUrl
        // adds it. Opening the raw "/jobs/uuid" path would hit the server and 404.
        const url = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(this.router.createUrlTree(path)));
        window.open(url, "_blank");
    }

    // Route segments to open a result (shared by same-window navigation and open-in-new-tab); null if it can't open.
    private resultPath(result: IGlobalSearchFlatResult): any[] | null {
        switch (result.resultType) {
            case "job": return result.uuid ? ["/jobs", result.uuid] : null;
            case "payable": return result.uuid ? ["/payables", result.uuid] : ["/payables"];
            case "customer": return result.uuid ? ["/customers", result.uuid] : null;
            case "vendor": return result.uuid ? ["/vendors", result.uuid] : null;
            case "subcontractor": return result.uuid ? ["/subcontractors", result.uuid] : null;
            case "equipment": return result.uuid ? ["/equipment", result.uuid] : null;
            case "invoice": return result.uuid ? ["/invoices", result.uuid] : null;
            case "payment": return result.uuid ? ["/payments", result.uuid] : null;
            case "bdAccount": return result.uuid ? ["/businessDevelopment", result.uuid] : null;
            case "bdContact": return result.uuid ? ["/businessDevelopment", result.uuid] : null;
            case "workOrder": return result.uuid ? ["/workOrders", result.uuid] : null;
            // Estimates / tasks / appointments open their parent JOB (the server sets uuid to the job's uuid).
            case "estimate": return result.uuid ? ["/jobs", result.uuid] : null;
            case "task": return result.uuid ? ["/jobs", result.uuid] : null;
            case "appointment": return result.uuid ? ["/jobs", result.uuid] : null;
        }
        return null;
    }

    private parsePrefix(text: string): { resultType: GlobalSearchResultType | null; query: string } {
        // Prefix: exactly 3 chars at position 0, followed by ':'.
        if (text.length >= 4 && text[3] === ":") {
            const prefix = text.substring(0, 3).toLowerCase();
            if (KNOWN_PREFIXES.includes(prefix)) {
                const query = text.substring(4).trim();
                return { resultType: PREFIX_TO_TYPE[prefix], query };
            }
        }
        return { resultType: null, query: text.trim() };
    }

    private typeToPrefix(type: GlobalSearchResultType): string {
        const entry = Object.entries(PREFIX_TO_TYPE).find(([, v]) => v === type);
        return entry ? entry[0] : "";
    }

    private flatten(byType: Map<GlobalSearchResultType, IGlobalSearchResult[]>): IGlobalSearchFlatResult[] {
        const flat: IGlobalSearchFlatResult[] = [];

        // Group order — jobs/customers first; BD next; reference data; financial types last
        const displayOrder: GlobalSearchResultType[] = [
            "job", "customer",
            "bdAccount", "bdContact",
            "subcontractor", "vendor", "equipment",
            "workOrder", "estimate", "task", "appointment",
            "invoice", "payment", "payable"
        ];

        for (const type of displayOrder) {
            const rows = byType.get(type) ?? [];
            for (const item of rows) {
                flat.push({
                    ...item,
                    resultType: type,
                    displayLabel: item.primaryText ?? ""
                });
            }
        }

        return flat;
    }

    typeLabel(type: GlobalSearchResultType): string {
        switch (type) {
            case "job": return "Job";
            case "customer": return "Customer";
            case "bdAccount": return "BD Account";
            case "bdContact": return "BD Contact";
            case "subcontractor": return "Subcontractor";
            case "vendor": return "Vendor";
            case "equipment": return "Equipment";
            case "invoice": return "Invoice";
            case "payment": return "Payment";
            case "payable": return "Payable";
            case "workOrder": return "Work Order";
            case "estimate": return "Estimate";
            case "task": return "Task";
            case "appointment": return "Appointment";
        }
    }

    typeIcon(type: GlobalSearchResultType): string {
        switch (type) {
            case "job": return "far fa-briefcase";
            case "customer": return "far fa-user";
            case "bdAccount": return "far fa-building";
            case "bdContact": return "far fa-id-card";
            case "subcontractor": return "far fa-hammer";
            case "vendor": return "far fa-truck";
            case "equipment": return "far fa-toolbox";
            case "invoice": return "far fa-file-invoice-dollar";
            case "payment": return "far fa-credit-card";
            case "payable": return "far fa-money-bill";
            case "workOrder": return "far fa-clipboard-list-check";
            case "estimate": return "far fa-file-signature";
            case "task": return "far fa-square-check";
            case "appointment": return "far fa-calendar";
        }
    }
}
